import type { ReactNode, VoidFunctionComponent } from 'react';
import { RouterLink } from '../Link';
import Tooltip from '../Tooltip';
import styles from './PageHeadingBreadcrumbs.module.less';

type BreadCrumbWithText = {
  //  Where the link should navigate to. Optional as sometimes you just want to put text in there.
  to?: string;
  //  Text inside of the breadcrumb.
  text: string;
};

type BreadCrumbWithContent = {
  content: ReactNode;
};

export type BreadCrumb =
  | BreadCrumbWithText
  | BreadCrumbWithContent;

type Props = BreadCrumb & {
  truncateAt?: number;
};

/**
 * A single "crumb" in the breadcrumbs
 *
 * @param {Props} props - PageHeadingBreadCrumb props
 * @param {string} props.text - Text for the breadcrumb.
 * @param {string} [props.to] - If a link crumb, this is the URL it should navigate to.
 * @param {number} [props.truncateAt] - length at which the crumb should be truncated (defaults to `0`/no truncation).
 */
const PageHeadingBreadCrumb: VoidFunctionComponent<Props> = (props) => {
  //  If we are passing in a content prop, render that first.
  if ('content' in props) {
    return (
      <span className={styles.crumb}>
        {props.content}
      </span>
    );
  }

  //  If there is no URL to navigate to, it's the last breadcrumb/shouldn't be a link.
  if (!props.to) {
    return (
      <span className={`${styles.crumb} ${styles.crumbText}`}>
        {props.text}
      </span>
    );
  }

  if (props.truncateAt && props.text.length >= props.truncateAt) {
    return (
      <Tooltip
        id={`page-heading-breadcrumb--${props.text}`}
        placement="top"
        tooltip={props.text}
      >
        <RouterLink
          to={props.to}
          className={`${styles.crumb} ${styles.crumbLink}`}
        >
          {`${props.text.slice(0, props.truncateAt)}...`}
        </RouterLink>
      </Tooltip>
    );
  }

  return (
    <RouterLink
      to={props.to}
      className={`${styles.crumb} ${styles.crumbLink}`}
    >
      {props.text}
    </RouterLink>
  );
};

export default PageHeadingBreadCrumb;
